import React, { Component } from "react";
import PropTypes from "prop-types";

import { head, last, noop } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import { saveNodeType, isHover } from "../utils";

import { getNewXY } from "./EachTrendLine";
import StraightLine, { generateLine } from "../components/StraightLine";
import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import Text from "../components/Text";

// Base dos wrappers de níveis Fibonacci (Retracement / Extension): constructor,
// 7 handlers, render de ~120 linhas e propTypes eram idênticos — 31 de ~422
// linhas diferiam (auditoria 2026-07-17, item 1c). Cada par vira subclasse com
// estáticos: defaultLevels, computeLevelY({level,y1,y2,dy}), percentDigits.
class EachFibLevels extends Component {
  constructor(props) {
    super(props);

    this.handleEdge1Drag = this.handleEdge1Drag.bind(this);
    this.handleEdge2Drag = this.handleEdge2Drag.bind(this);

    this.handleLineNSResizeTop = this.handleLineNSResizeTop.bind(this);
    this.handleLineNSResizeBottom = this.handleLineNSResizeBottom.bind(this);
    this.handleLineMove = this.handleLineMove.bind(this);

    this.handleLineDragStart = this.handleLineDragStart.bind(this);

    this.handleHover = this.handleHover.bind(this);

    this.isHover = isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.state = {
      hover: false,
    };
  }
  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }
  handleLineDragStart() {
    const { x1, y1, x2, y2 } = this.props;

    this.dragStart = {
      x1,
      y1,
      x2,
      y2,
    };
  }
  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;

    const {
      x1: x1Value,
      y1: y1Value,
      x2: x2Value,
      y2: y2Value,
    } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(
      xScale,
      xAccessor,
      [x1 - dx, y1 - dy],
      fullData,
    );
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(
      xScale,
      xAccessor,
      [x2 - dx, y2 - dy],
      fullData,
    );
    const newY2Value = yScale.invert(y2 - dy);

    onDrag(index, {
      x1: newX1Value,
      y1: newY1Value,
      x2: newX2Value,
      y2: newY2Value,
    });
  }
  handleLineNSResizeTop(moreProps) {
    const { index, onDrag } = this.props;
    const { x1, x2, y2 } = this.props;

    const [, y1] = getNewXY(moreProps);

    onDrag(index, {
      x1,
      y1,
      x2,
      y2,
    });
  }
  handleLineNSResizeBottom(moreProps) {
    const { index, onDrag } = this.props;
    const { x1, y1, x2 } = this.props;

    const [, y2] = getNewXY(moreProps);

    onDrag(index, {
      x1,
      y1,
      x2,
      y2,
    });
  }
  handleEdge1Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { y1, x2, y2 } = this.props;

    const [x1] = getNewXY(moreProps);

    onDrag(index, {
      x1,
      y1,
      x2,
      y2,
    });
  }
  handleEdge2Drag(moreProps) {
    const { index, onDrag } = this.props;
    const { x1, y1, y2 } = this.props;

    const [x2] = getNewXY(moreProps);

    onDrag(index, {
      x1,
      y1,
      x2,
      y2,
    });
  }
  render() {
    const { x1, x2, y1, y2 } = this.props;
    const { interactive, yDisplayFormat, type, appearance } = this.props;

    // Extract values from registry format consistently
    const stroke = appearance.stroke?.color || "#FF9800";
    const strokeWidth = appearance.stroke?.width || 2;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray = appearance.stroke?.dash || "Solid";

    const fontFamily =
      appearance.font?.fontFamily ||
      "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = appearance.font?.fontSize || 11;
    const fontFill = appearance.font?.color || "#FF9800";

    const { r } = appearance;

    // Get theme-aware edge colors for maximum visibility
    const edgeColors = {
      stroke: "#000000", // Black stroke
      fill: "#FFFFFF", // White fill
    };

    const actualEdgeFill = edgeColors.fill;
    // Force larger radius for better visibility
    const circleRadius = r || 6;
    const { hoverText, selected } = this.props;
    const { hover } = this.state;
    const { onDragComplete } = this.props;
    const configuredLevels =
      appearance.levels || this.constructor.defaultLevels;
    const activeLevels = configuredLevels.filter((l) => l.display !== false);
    const lines = helper({
      x1,
      x2,
      y1,
      y2,
      levels: activeLevels,
      computeLevelY: this.constructor.computeLevelY,
    });

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const lineType =
      type === "EXTEND" ? "XLINE" : type === "BOUND" ? "LINE" : type;
    const dir = head(lines).y1 > last(lines).y1 ? 3 : -1.3;

    return (
      <g>
        {lines.map((line, j) => {
          const text = `${yDisplayFormat(line.y)} (${line.percent.toFixed(
            this.constructor.percentDigits,
          )}%)`;

          const xyProvider = ({ xScale, chartConfig }) => {
            const { yScale } = chartConfig;
            const { x1, y1, x2 } = generateLine({
              type: lineType,
              start: [line.x1, line.y],
              end: [line.x2, line.y],
              xScale,
              yScale,
            });

            const x = xScale(Math.min(x1, x2)) + 10;
            const y = yScale(y1) + dir * 4;
            return [x, y];
          };

          const firstOrLast = j === 0 || j === lines.length - 1;

          const interactiveCursorClass = firstOrLast
            ? "react-stockcharts-ns-resize-cursor"
            : "react-stockcharts-move-cursor";

          const interactiveEdgeCursorClass = firstOrLast
            ? "react-stockcharts-ns-resize-cursor"
            : "react-stockcharts-ew-resize-cursor";

          const dragHandler =
            j === 0
              ? this.handleLineNSResizeTop
              : j === lines.length - 1
                ? this.handleLineNSResizeBottom
                : this.handleLineMove;

          const edge1DragHandler =
            j === 0
              ? this.handleLineNSResizeTop
              : j === lines.length - 1
                ? this.handleLineNSResizeBottom
                : this.handleEdge1Drag;
          const edge2DragHandler =
            j === 0
              ? this.handleLineNSResizeTop
              : j === lines.length - 1
                ? this.handleLineNSResizeBottom
                : this.handleEdge2Drag;

          const hoverHandler = interactive
            ? { onHover: this.handleHover, onUnHover: this.handleHover }
            : {};
          return (
            <g key={j}>
              <StraightLine
                ref={this.saveNodeType(`line_${j}`)}
                selected={selected || hover}
                {...hoverHandler}
                type={lineType}
                x1Value={line.x1}
                y1Value={line.y}
                x2Value={line.x2}
                y2Value={line.y}
                stroke={stroke}
                strokeWidth={hover || selected ? strokeWidth + 1 : strokeWidth}
                strokeOpacity={strokeOpacity}
                strokeDasharray={strokeDasharray}
                interactiveCursorClass={interactiveCursorClass}
                onDragStart={this.handleLineDragStart}
                onDrag={dragHandler}
                onDragComplete={onDragComplete}
              />
              <Text
                selected={selected}
                xyProvider={xyProvider}
                fontFamily={fontFamily}
                fontSize={fontSize}
                fill={fontFill}
              >
                {text}
              </Text>
              <ClickableCircle
                ref={this.saveNodeType("edge1")}
                show={selected || hover}
                cx={line.x1}
                cy={line.y}
                r={circleRadius}
                fill={actualEdgeFill}
                stroke={edgeColors.stroke}
                strokeWidth={2}
                strokeOpacity={1}
                interactiveCursorClass={interactiveEdgeCursorClass}
                onDrag={edge1DragHandler}
                onDragComplete={onDragComplete}
              />
              <ClickableCircle
                ref={this.saveNodeType("edge2")}
                show={selected || hover}
                cx={line.x2}
                cy={line.y}
                r={circleRadius}
                fill={actualEdgeFill}
                stroke={edgeColors.stroke}
                strokeWidth={2}
                strokeOpacity={1}
                interactiveCursorClass={interactiveEdgeCursorClass}
                onDrag={edge2DragHandler}
                onDragComplete={onDragComplete}
              />
            </g>
          );
        })}
        <HoverTextNearMouse
          show={hoverTextEnabled && hover}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }
}

function helper({ x1, y1, x2, y2, levels, computeLevelY }) {
  const dy = y2 - y1;
  return levels.map((each) => ({
    percent: each.level,
    x1,
    x2,
    y: computeLevelY({ level: each.level, y1, y2, dy }),
  }));
}

export const fibLevelsPropTypes = {
  x1: PropTypes.any.isRequired,
  x2: PropTypes.any.isRequired,
  y1: PropTypes.number.isRequired,
  y2: PropTypes.number.isRequired,

  yDisplayFormat: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,

  appearance: PropTypes.shape({
    // Níveis configurados (FibLevelsType do registry); ausente → defaultLevels
    levels: PropTypes.array,
    // Registry format (primary)
    stroke: PropTypes.oneOfType([
      PropTypes.shape({
        color: PropTypes.string,
        dash: PropTypes.string,
        width: PropTypes.number,
        opacity: PropTypes.number,
      }),
      PropTypes.string, // Legacy support
    ]),
    font: PropTypes.shape({
      fontFamily: PropTypes.string,
      fontSize: PropTypes.number,
      color: PropTypes.string,
    }),
    // Legacy properties (fallback support)
    strokeWidth: PropTypes.number,
    strokeOpacity: PropTypes.number,
    fontFamily: PropTypes.string,
    fontSize: PropTypes.number,
    fontFill: PropTypes.string,
    // Other properties
    edgeFill: PropTypes.string.isRequired,
    nsEdgeFill: PropTypes.string.isRequired,
    edgeStroke: PropTypes.shape({
      color: PropTypes.string,
      dash: PropTypes.string,
      width: PropTypes.number,
      opacity: PropTypes.number,
    }).isRequired,
    r: PropTypes.number.isRequired,
  }).isRequired,

  interactive: PropTypes.bool.isRequired,
  hoverText: PropTypes.object.isRequired,

  index: PropTypes.number,
  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
};

export const fibLevelsDefaultProps = {
  yDisplayFormat: (d) => d.toFixed(2),
  interactive: true,

  appearance: {
    stroke: "#000000",
    strokeWidth: 1,
    strokeOpacity: 1,
    fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
    fontSize: 10,
    fontFill: "#000000",
    edgeStroke: { color: "#000000", dash: "Solid", width: 1, opacity: 1 },
    edgeFill: "#FFFFFF",
    nsEdgeFill: "#000000",
    r: 5,
  },
  selected: false,

  onDrag: noop,
  onDragComplete: noop,

  hoverText: {
    enable: false,
  },
};

EachFibLevels.propTypes = fibLevelsPropTypes;
EachFibLevels.defaultProps = fibLevelsDefaultProps;

export default EachFibLevels;
